/**
 * District-centroid resolution for the flood-affected-areas map layer.
 *
 * Pure, dependency-light helpers (no deck.gl / React) so the ธานโต spelling
 * alias and the "surface, don't silently drop, an unmatched district" contract
 * are pinned by unit tests instead of by memory.
 */

import type { Feature, Polygon, MultiPolygon } from "geojson";

/** One map point per flood-affected district (data.go.th / yala-province). */
export interface FloodAffectedDistrictPoint {
  district: string;
  villages: number;
  lng: number;
  lat: number;
}

/**
 * data.go.th spells อ.ธารโต with a น ("ธานโต"); the boundary geojson uses ร
 * ("ธารโต"). Alias the known variants so every affected district still matches a
 * centroid. Add an entry here if the province introduces another spelling.
 */
export const DISTRICT_NAME_ALIASES: Record<string, string> = {
  "ธานโต": "ธารโต",
};

/**
 * Rough centroid of a district polygon — the mean of its vertices. Good enough
 * to anchor one bubble per district; biased by vertex density and (for a very
 * concave polygon) could sit just outside it, which is fine for a marker.
 * Returns `fallback` for empty geometry.
 */
export function polygonCentroid(geom: Polygon | MultiPolygon, fallback: [number, number]): [number, number] {
  const rings = geom.type === "Polygon" ? geom.coordinates : geom.coordinates.flat();
  const pts = rings.flat();
  if (pts.length === 0) return fallback;
  const [sx, sy] = pts.reduce(([ax, ay], [x, y]) => [ax + x, ay + y], [0, 0]);
  return [sx / pts.length, sy / pts.length];
}

/** Build a `nameTh → centroid` lookup from district boundary features. */
export function buildDistrictCentroids(
  features: Array<Feature<Polygon | MultiPolygon, Record<string, unknown>>>,
  fallback: [number, number],
): Map<string, [number, number]> {
  const map = new Map<string, [number, number]>();
  for (const f of features) {
    const nameTh = String(f.properties?.nameTh ?? "").trim();
    if (nameTh && f.geometry) map.set(nameTh, polygonCentroid(f.geometry, fallback));
  }
  return map;
}

/**
 * Resolve affected-district rows to map points. Districts with no centroid (after
 * the alias fallback) are returned in `unmatched` so the caller can surface them
 * (a dev warning) rather than let them vanish — a future rename stays loud.
 */
export function resolveFloodAffectedPoints(
  rows: Array<{ district: string; villages: number }>,
  centroids: Map<string, [number, number]>,
): { points: FloodAffectedDistrictPoint[]; unmatched: string[] } {
  const points: FloodAffectedDistrictPoint[] = [];
  const unmatched: string[] = [];
  for (const { district, villages } of rows) {
    const key = district.trim();
    const c = centroids.get(key) ?? centroids.get(DISTRICT_NAME_ALIASES[key] ?? "");
    if (!c) {
      unmatched.push(key);
      continue;
    }
    points.push({ district, villages, lng: c[0], lat: c[1] });
  }
  return { points, unmatched };
}
